<template>
  <!-- A caixa tem VÁRIOS alvos quando há o que editar: o cabeçalho leva ao design da
       coleção, cada linha à propriedade dela, o compartimento de fluxo ao fluxo.

       Sem o que editar — ambiente encerrado, ou quem não desenha — ela volta a ser UM: não há
       propriedade a abrir nem fluxo a mexer, e sobra a coleção, que continua tendo o que
       mostrar. Aí o alvo é a caixa inteira — destacar uma linha que não leva a lugar nenhum
       prometeria o que não há. -->
  <div class="class-box" :class="{'class-box-target': !canWrite}" :style="style"
       :role="canWrite ? undefined : 'button'" :tabindex="canWrite ? undefined : 0"
       :title="canWrite ? undefined : $tt('collection-goto', {collection: model.name})"
       @click="canWrite || open({kind: 'class'})"
       @keydown.enter="canWrite || open({kind: 'class'})"
       @keydown.space.prevent="canWrite || open({kind: 'class'})">
    <!-- role/tabindex e não v-btn: são blocos de altura fixa, e o v-btn imporia altura,
         centralização e caixa-alta ao conteúdo. -->
    <div class="class-box-header" :class="{'class-box-target': canWrite}"
         :role="canWrite ? 'button' : undefined" :tabindex="canWrite ? 0 : undefined"
         :title="canWrite ? $tt('collection-design-goto', {collection: model.name}) : undefined"
         @click="canWrite && open({kind: 'class'})"
         @keydown.enter="canWrite && open({kind: 'class'})"
         @keydown.space.prevent="canWrite && open({kind: 'class'})">
      <v-icon size="small" start color="white">mdi-table</v-icon>
      <span class="class-box-title">{{ model.name }}</span>
    </div>

    <!-- Dois compartimentos com o MESMO markup: o que os separa é fazer ou não parte do
         identificador, e isso corta atributos e relacionamentos por igual. -->
    <div v-for="compartment of compartments" :key="compartment.kind" class="class-box-compartment">
      <div v-for="row of compartment.rows" :key="row.alias"
           class="class-box-row" :class="{'is-key': row.identifier, 'class-box-target': canWrite}"
           :role="canWrite ? 'button' : undefined" :tabindex="canWrite ? 0 : undefined"
           :title="rowTitle(row)"
           @click="canWrite && open({kind: 'property', property: row.alias})"
           @keydown.enter="canWrite && open({kind: 'property', property: row.alias})"
           @keydown.space.prevent="canWrite && open({kind: 'property', property: row.alias})">
        <span class="class-box-markers">
          <v-icon v-if="row.identifier" size="x-small" color="amber-darken-3">mdi-key</v-icon>
          <!-- A seta diz NAVEGABILIDADE, não quem é o dono: dupla quando as duas coleções
               têm propriedade uma para a outra, simples quando a ligação só existe de cá
               para lá. Qual dos lados guarda o dado está no title e no design da coleção. -->
          <v-icon v-if="row.target" size="x-small" color="teal">
            {{ row.target.bidirectional ? 'mdi-arrow-left-right' : 'mdi-arrow-right' }}
          </v-icon>
          <!-- A seleção também aponta para fora da caixa, só que para um domínio: a
               marca é o que diz isso na linha, e a seta até a caixa dele diz para qual. -->
          <v-icon v-if="row.domain" size="x-small" color="orange-darken-4">
            mdi-format-list-bulleted
          </v-icon>
          <v-icon v-if="row.required" size="x-small" :class="{'conditional': row.required.condition}"
                  color="red-darken-1">mdi-asterisk</v-icon>
          <!-- Exclusivo não se repete no identificador: fazer parte da chave já diz que
               participa da unicidade, e a chavinha ao lado tornaria as duas marcas
               redundantes justamente onde o espaço é mais disputado. -->
          <v-icon v-if="row.unique && !row.identifier" size="x-small"
                  :class="{'conditional': row.unique.condition}" color="blue-grey">mdi-fingerprint</v-icon>
          <v-icon v-if="row.computed" size="x-small" color="purple">mdi-function-variant</v-icon>
        </span>
        <span class="class-box-name">{{ row.name }}</span>
        <span class="class-box-type">{{ row.typeLabel }}</span>
        <span class="class-box-multiplicity">{{ row.multiplicity }}</span>
        <!-- Na UML o valor default fecha a linha do atributo, depois da multiplicidade e com
             um "=": `nome: Tipo [mult] = valor`. Só o NOME da opção aparece — o id é interno.
             Sem default, o span inteiro some, que é o caso da maioria dos atributos. -->
        <span v-if="row.defaultValue" class="class-box-default">= {{ row.defaultValue }}</span>
      </div>
    </div>

    <!-- O fluxo é UM alvo, não um por estado: a ligação da coleção é com o fluxo inteiro,
         e os estados são o conteúdo dele. -->
    <div v-if="model.workflow" class="class-box-compartment class-box-target"
         role="button" tabindex="0"
         :title="$tt('workflow-design-named', {workflow: model.workflow.name})"
         @click="open({kind: 'workflow'})"
         @keydown.enter="open({kind: 'workflow'})" @keydown.space.prevent="open({kind: 'workflow'})">
      <div class="class-box-section">
        <v-icon size="x-small" start>mdi-sync</v-icon>{{ model.workflow.name }}
      </div>
      <div v-for="state of model.workflow.states" :key="state.id"
           class="class-box-row" :title="stateTitle(state)">
        <span class="class-box-markers">
          <v-icon size="x-small" :color="state.color">mdi-circle-medium</v-icon>
        </span>
        <span class="class-box-name">{{ state.name }}</span>
        <span class="class-box-type">{{ stateMark(state) }}</span>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {ClassModel, ClassRow, ClassState, BoxTarget} from '@/utils/classModel'
import {boxProperties} from '@/utils/classDiagram'
import {MessageKey} from '@/i18n'

/**
 * Uma caixa do diagrama de classes: cabeçalho e até três compartimentos — atributos,
 * relacionamentos e fluxo.
 *
 * Não decide o que mostrar: isso é do classModel, para que a caixa e o cálculo de
 * altura leiam a mesma lista. Compartimento sem linha não é renderizado, e o boxHeight
 * também não o conta — é o que mantém o desenho e as linhas do diagrama alinhados.
 *
 * As métricas chegam como custom properties para não haver dois números: o que o layout
 * somou é o que o CSS desenha.
 */
@Component({emits: ['open'], name: 'ontos-class-box'})
class ClassBox extends Vue {
  @Prop({type: Object, required: true}) readonly model!: ClassModel
  /**
   * Há o que editar aqui? Decide se a caixa tem vários alvos ou um só.
   *
   * Booleana, e não o nível: a pergunta desta caixa é local, e quem a responde é o diagrama,
   * com `can('designer')`. Por prop, e não lendo o ambiente: ela desenha um MODELO, e não
   * conhece workspace.
   */
  @Prop({type: Boolean, default: true}) readonly canWrite!: boolean

  //exposto ao template: função de módulo não é enxergada de lá
  style = boxProperties()

  /**
   * Diz o que foi clicado e deixa o diagrama decidir se o clique vale.
   *
   * Emite em vez de agir porque só o diagrama sabe se o ponteiro andou: um arrasto que
   * termina sobre uma propriedade não pode abrir modal nenhum.
   */
  open(target: BoxTarget) {
    this.$emit('open', target)
  }

  /** Compartimento vazio não vira faixa — é o que o boxHeight também assume. */
  get compartments() {
    return [
      {kind: 'key', rows: this.model.identifiers},
      {kind: 'other', rows: this.model.others}
    ].filter(compartment => compartment.rows.length)
  }

  /** O title carrega o que não cabe na linha: alias, condições e as duas cardinalidades. */
  rowTitle(row: ClassRow): string {
    const parts = [`${row.name} · ${row.alias}`, row.typeLabel]
    if (row.identifier) parts.push(this.$tt('definition.identifier') as string)
    if (row.target) {
      parts.push(`${row.target.inverseMultiplicity} → ${row.multiplicity}`)
      parts.push(this.$tt(row.target.mirror ? 'property-kind-reverse' : 'property-kind-relationship') as string)
    }
    //A linha escreve o domínio no lugar do tipo, então é o TIPO que falta ali — do mesmo
    //jeito que o relacionamento diz "Relacionamento" ao lado da coleção de destino.
    if (row.domain) parts.push(this.$tt('type.choice') as string)
    if (row.defaultValue) parts.push(`${this.$tt('default-value')}: ${row.defaultValue}`)
    parts.push(this.markerText('required', row.required))
    parts.push(this.markerText('unique', row.unique))
    //O que o clique faz, no fim: o realce diz que dá para clicar, o tooltip diz no quê
    parts.push(this.$tt('edit') as string)
    return parts.filter(part => !!part).join(' · ')
  }

  markerText(key: MessageKey, marker: { condition: string } | null): string {
    if (!marker) return ''
    const label = this.$tt(key) as string
    return marker.condition ? `${label}: ${marker.condition}` : label
  }

  stateMark(state: ClassState): string {
    if (state.initial) return this.$tt('state-initial') as string
    if (state.final) return this.$tt('state-final') as string
    return ''
  }

  stateTitle(state: ClassState): string {
    const mark = this.stateMark(state)
    return mark ? `${state.name} · ${mark}` : state.name
  }
}

export default toNative(ClassBox)
</script>
<!-- O desenho é o mesmo da caixa de domínio: uma folha só, dois componentes. -->
<style scoped src="./box.css"></style>
